import type { FieldValues, UseControllerProps } from "react-hook-form";

import { convertHtmlToText } from "utils/common";

type RequiredRule = NonNullable<
  NonNullable<UseControllerProps<FieldValues>["rules"]>["required"]
>;

export interface ResolvedRequiredRule {
  /** True when either the `required` prop or `rules.required` demands a value. */
  isRequired: boolean;
  /** The message to show for an empty value — the caller's, else `${label} is required`. */
  message: string;
  /** What to hand RHF as `rules.required`: the caller's rule wins, the prop is the fallback. */
  controlRequired: RequiredRule;
}

/**
 * Reconciles the two ways a `WordProcessorField` can be marked required.
 *
 * ENG-1944: `controlRules` used to spread the caller's `rules` and then
 * unconditionally overwrite `required` with `{ value: Boolean(required) }`, and
 * the internal emptiness `validate` was gated on the same prop. So every caller
 * that passed `rules={{ required: "..." }}` WITHOUT the `required` prop had dead
 * validation — the Custom Campaign wizard's SMS body and email subject among
 * them. Both sources now arm the check.
 */
export const resolveRequiredRule = ({
  required,
  rules,
  label
}: {
  required?: boolean;
  rules?: { required?: RequiredRule };
  label?: string;
}): ResolvedRequiredRule => {
  const callerRule = rules?.required;
  const fallbackMessage = `${label || "Field"} is required`;

  let isRequiredByRules = false;
  let callerMessage: string | undefined;
  if (typeof callerRule === "string") {
    isRequiredByRules = callerRule.length > 0;
    callerMessage = callerRule || undefined;
  } else if (typeof callerRule === "object" && callerRule !== null) {
    isRequiredByRules = Boolean(callerRule.value);
    callerMessage = callerRule.message || undefined;
  } else {
    isRequiredByRules = Boolean(callerRule);
  }

  const message = callerMessage ?? fallbackMessage;

  return {
    isRequired: Boolean(required) || isRequiredByRules,
    message,
    controlRequired: callerRule ?? { value: Boolean(required), message }
  };
};

/**
 * True when the editor holds no visible text. TinyMCE reports an emptied field
 * as `<p></p>`, `<p><br></p>` or `<p>&nbsp;</p>`, all of which pass RHF's
 * built-in `required` (a non-empty string) — the text projection is what decides.
 */
export const isWordProcessorValueEmpty = (value: unknown): boolean =>
  !convertHtmlToText(String(value ?? "")).trim().length;
